import { useContext } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ContactInformationSchema, ContactInformationType } from '../../types';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import ErrorIcon from '../../../../_sharedComponents/error-icon';
import StepHeader from '../../step-header';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function ContactInformation() {
	const financialContext = useContext(FinancialStatusContext);
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<ContactInformationType>({
		defaultValues: {
			firstName: '',
			lastName: '',
			middleName: '',
			DOB: '',
			phone: '',
			email: '',
		},
		shouldUnregister: false,
		resolver: zodResolver(ContactInformationSchema),
	});

	const onSubmit = (data: ContactInformationType) => {
		financialContext.updateContactInformation(data);
		financialContext.updateCurrentStep(2);
	};

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between">
				<StepHeader step={1} />
				{/* First Row */}
				<div className="flex min-h-24 flex-col justify-between space-y-5 md:space-y-0 lg:flex-row">
					<div className="min-h-24 md:w-fit">
						<Label currentStep="contactInformation" value="firstName" />
						<div className="relative">
							<input
								{...register('firstName')}
								className={`${styles.mediumInput} ${errors.firstName ? styles.error : ''}`}
								id="firstName"
								placeholder={financialContext.getPlaceholder({ step: 'contactInformation', value: 'firstName' })}
								type="text"
							/>
							{errors.firstName ? <ErrorIcon /> : null}
						</div>
						{errors.firstName ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="contactInformation" value="firstName" />
							</div>
						) : null}
					</div>
					<div className="min-h-24 md:w-fit">
						<div>
							<Label currentStep="contactInformation" value="middleName" />
							<div className="relative">
								<input
									{...register('middleName')}
									className={styles.mediumInput}
									id="middleName"
									placeholder={financialContext.getPlaceholder({ step: 'contactInformation', value: 'middleName' })}
									type="text"
								/>
							</div>
						</div>
					</div>
					<div className="min-h-24 md:w-fit">
						<Label currentStep="contactInformation" value="lastName" />
						<div className="relative">
							<input
								{...register('lastName')}
								className={`${styles.mediumInput} ${errors.lastName ? styles.error : ''}`}
								id="lastName"
								placeholder={financialContext.getPlaceholder({ step: 'contactInformation', value: 'lastName' })}
								type="text"
							/>
							{errors.lastName ? <ErrorIcon /> : null}
						</div>
						{errors.lastName ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="contactInformation" value="lastName" />
							</div>
						) : null}
					</div>
				</div>
				{/* Second Row */}
				<div className="mt-5 flex min-h-24 flex-col justify-between space-y-5 md:mt-0 md:space-y-0 lg:flex-row">
					<div className="min-h-24 md:w-fit">
						<Label currentStep="contactInformation" value="DOB" />
						<div className="relative">
							<input
								{...register('DOB')}
								className={`${styles.mediumInput} ${errors.DOB ? styles.error : ''}`}
								id="DOB"
								placeholder={financialContext.getPlaceholder({ step: 'contactInformation', value: 'DOB' })}
								type="text"
							/>
							{errors.DOB ? <ErrorIcon /> : null}
						</div>
						{errors.DOB ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="contactInformation" value="DOB" />
							</div>
						) : null}
					</div>
					<div className="min-h-24 md:w-fit">
						<Label currentStep="contactInformation" value="phone" />
						<div className="relative">
							<input
								{...register('phone')}
								className={`${styles.mediumInput} ${errors.phone ? styles.error : ''}`}
								id="phone"
								placeholder={financialContext.getPlaceholder({ step: 'contactInformation', value: 'phone' })}
								type="text"
							/>
							{errors.phone ? <ErrorIcon /> : null}
						</div>
						{errors.phone ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="contactInformation" value="phone" />
							</div>
						) : null}
					</div>
					<div className="min-h-24 md:w-fit">
						<Label currentStep="contactInformation" value="email" />
						<div className="relative">
							<input
								{...register('email')}
								className={`${styles.mediumInput} ${errors.email ? styles.error : ''}`}
								id="email"
								placeholder={financialContext.getPlaceholder({ step: 'contactInformation', value: 'email' })}
								type="text"
							/>
							{errors.email ? <ErrorIcon /> : null}
						</div>
						{errors.email ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="contactInformation" value="email" />
							</div>
						) : null}
					</div>
				</div>
				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(0);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>

					<Button label={financialContext.language === 'en' ? 'Next' : 'Seguiente'} type="submit" />
				</div>
			</div>
		</form>
	);
}
